import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useParams } from 'react-router'

import { FALLBACK_LANGUAGE } from '@app/i18n'

import { isValidLocale, localizeUrl, type SupportedLocale } from './utils'


/**
 * Layout component that handles locale from URL params.
 * Wraps content inside /:locale route.
 */
export function LocaleLayout () {
  const { locale } = useParams<{ locale: string }>()
  const { i18n } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    // Validate locale param
    if (!locale || !isValidLocale(locale)) {
      // Redirect to default locale
      const defaultLocale = (i18n.language || FALLBACK_LANGUAGE) as SupportedLocale
      const currentPath = window.location.pathname.replace(/^\/[^/]*/, '') || '/'
      const search = window.location.search
      const hash = window.location.hash
      const localizedPath = localizeUrl(currentPath + search + hash, defaultLocale)
      navigate(localizedPath, { replace: true })
      return
    }

    // Sync i18n with URL locale
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale)
    }
  }, [ locale, i18n, navigate ])

  // Don't render until we have a valid locale
  if (!locale || !isValidLocale(locale)) {
    return null
  }

  return <Outlet />
}
